import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import {useFinancial} from '../context/FinancialContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCheck,FiArrowRight,FiTarget,FiShield,FiDollarSign,FiCalendar,FiBook,FiAward,FiHome,FiTruck,FiHeart,FiGlobe,FiBriefcase,FiSun,FiMoreHorizontal}=FiIcons;

const GOAL_TYPES=[ 
  {id: 'child-education',name: 'Child Education',icon: FiBook},
  {id: 'higher-education',name: 'Higher Education',icon: FiAward},
  {id: 'house-purchase',name: 'House Purchase',icon: FiHome},
  {id: 'car-purchase',name: 'Car Purchase',icon: FiTruck},
  {id: 'wedding',name: 'Wedding',icon: FiHeart},
  {id: 'vacation',name: 'Vacation/Travel',icon: FiGlobe},
  {id: 'emergency-fund',name: 'Emergency Fund',icon: FiShield},
  {id: 'business',name: 'Business Investment',icon: FiBriefcase},
  {id: 'retirement',name: 'Retirement Top-up',icon: FiSun},
  {id: 'other',name: 'Other',icon: FiMoreHorizontal} 
];

function ConversationalOnboarding() {
  const {dispatch}=useFinancial();
  const navigate=useNavigate();
  const [formData,setFormData]=useState({name: '',age: '',income: '',expenses: '',emergencyFund: '',selectedGoals: [],goalDetails: {}});
  const [errors,setErrors]=useState({});

  const handleInputChange=(field,value)=> {
    setFormData(prev=> ({...prev,[field]: value}));
    if (errors[field]) {setErrors(prev=> ({...prev,[field]: null}));}
  };

  const handleGoalToggle=(goalId)=> {
    setFormData(prev=> {
      const isSelected=prev.selectedGoals.includes(goalId);
      const newSelectedGoals=isSelected ? prev.selectedGoals.filter(id=> id !==goalId) : [...prev.selectedGoals,goalId];
      const newGoalDetails={...prev.goalDetails};
      if (!isSelected && !newGoalDetails[goalId]) {newGoalDetails[goalId]={amount: '',timeHorizon: ''};} 
      return {...prev,selectedGoals: newSelectedGoals,goalDetails: newGoalDetails};
    });
  };

  const handleGoalDetailChange=(goalId,field,value)=> {
    setFormData(prev=> ({...prev,goalDetails: {...prev.goalDetails,[goalId]: {...prev.goalDetails[goalId],[field]: value}}}));
  };

  const validateForm=()=> {
    const newErrors={};
    if (!formData.name?.trim()) newErrors.name='Required';
    if (!formData.age || formData.age < 18 || formData.age > 100) newErrors.age='Valid age required';
    if (!formData.income || formData.income <=0) newErrors.income='Required';
    if (!formData.expenses || formData.expenses <=0) newErrors.expenses='Required';
    if (formData.selectedGoals.length===0) newErrors.goals='Select at least one goal';
    formData.selectedGoals.forEach(goalId=> {
      const details=formData.goalDetails[goalId];
      if (!details?.amount || details.amount <=0) {newErrors[`goal_${goalId}_amount`]='Required';} 
      if (!details?.timeHorizon || details.timeHorizon <=0) {newErrors[`goal_${goalId}_timeHorizon`]='Required';}
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length===0;
  };

  const handleSubmit=()=> {
    if (!validateForm()) return;
    const userProfile={
      name: formData.name,
      currentAge: parseInt(formData.age),
      retirementAge: 60,
      lifeExpectancy: 80,
      monthlyIncome: parseInt(formData.income),
      monthlyExpenses: parseInt(formData.expenses),
      emergencyFund: parseInt(formData.emergencyFund) || 0,
      incomeGrowthRate: 8,
      expenseGrowthRate: 6,
      insurance: {termInsurance: 0,healthInsurance: 0,termPremium: 0,healthPremium: 0},
      returnExpectations: {shortTerm: 7,mediumTerm: 10,longTerm: 12},
      inflationRates: {general: 6,education: 11,medical: 10},
      existingInvestments: {fixedDeposits: 0,mutualFunds: 0,stocks: 0,epfPpf: 0,ssyNsc: 0,chitFund: 0,gold: 0,realEstate: 0,others: 0,bonds: 0,silver: 0,copper: 0,pms: 0,crypto: 0},
      assets: {bankSavings: 0,vehicles: 0,insurance: 0},
      liabilities: {homeLoan: 0,carLoan: 0,personalLoan: 0,creditCard: 0,educationLoan: 0,others: 0}
    };
    dispatch({type: 'UPDATE_USER_PROFILE',payload: userProfile});
    formData.selectedGoals.forEach(goalId=> {
      const goalType=GOAL_TYPES.find(g=> g.id===goalId);
      const details=formData.goalDetails[goalId];
      const goalData={
        name: goalType.name,
        type: 'Lump Sum',
        timeHorizon: parseInt(details.timeHorizon),
        goalAmount: parseInt(details.amount),
        inflationRate: goalId.includes('education') ? 11 : 6,
        expectedReturn: parseInt(details.timeHorizon) <=3 ? 7 : parseInt(details.timeHorizon) <=7 ? 10 : 12,
        taggedAssets: [],
        sipContribution: 0,
        lumpSumContribution: 0,
        whatIfPercentage: 40
      };
      dispatch({type: 'ADD_GOAL',payload: goalData});
    });
    navigate('/investments');
  };

  const formatCurrency=(value)=> {
    if (!value) return '';
    return new Intl.NumberFormat('en-IN',{style: 'currency',currency: 'INR',minimumFractionDigits: 0}).format(value);
  };

  return (
    <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div initial={{opacity: 0,y: -20}} animate={{opacity: 1,y: 0}} className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Let's Plan Your Financial Future</h1>
          <p className="text-lg text-gray-600">Simply fill in the blanks to get started</p>
        </motion.div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
          <div className="space-y-8 text-lg leading-relaxed text-gray-800">
            <div className="flex flex-wrap items-center gap-2">
              <span>Hello! My name is</span>
              <input type="text" value={formData.name} onChange={(e)=> handleInputChange('name',e.target.value)} placeholder="your name" className={`inline-flex px-3 py-1 border-b-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:border-[#19b48d] outline-none bg-transparent min-w-[200px]`} />
              <span>and I'm</span>
              <input type="number" value={formData.age} onChange={(e)=> handleInputChange('age',e.target.value)} placeholder="age" className={`inline-flex px-3 py-1 border-b-2 ${errors.age ? 'border-red-500' : 'border-gray-300'} focus:border-[#19b48d] outline-none bg-transparent w-20 text-center`} />
              <span>years old.</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span>My monthly income is ₹</span>
              <input type="number" value={formData.income} onChange={(e)=> handleInputChange('income',e.target.value)} placeholder="amount" className={`inline-flex px-3 py-1 border-b-2 ${errors.income ? 'border-red-500' : 'border-gray-300'} focus:border-[#19b48d] outline-none bg-transparent min-w-[150px]`} />
              <span>and expenses are ₹</span>
              <input type="number" value={formData.expenses} onChange={(e)=> handleInputChange('expenses',e.target.value)} placeholder="amount" className={`inline-flex px-3 py-1 border-b-2 ${errors.expenses ? 'border-red-500' : 'border-gray-300'} focus:border-[#19b48d] outline-none bg-transparent min-w-[150px]`} />
              <span>per month.</span>
            </div>
          </div>
          <div className="flex justify-end pt-12">
            <button onClick={handleSubmit} className="flex items-center space-x-2 px-8 py-3 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085] transition-colors duration-200 text-base font-medium">
              <span>Continue to Investments</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ConversationalOnboarding;